import pluralize from 'pluralize'

import { Address, EditLineItem, Maybe, Money } from '../../types'
import { NewStoreOrder } from '../types'

import useConfig from '@/gf/hooks/useConfig'
import MoneyM from '@/gf/modules/Money'
import ProductM from '@/gf/modules/Product'
import User from '@/gf/modules/User'
import useSession from '../hooks/useSession'

import AddressInput from '@/gf/components/AddressInput'
import Card from '@/gf/components/Card'
import Checkbox from '@/gf/components/Checkbox'
import Field from '@/gf/components/Field'
import { InStockPill, LeadTimePill } from '@/gf/components/LineItem'
import LinkButton from '@/gf/components/LinkButtonOld'
import PickupField from '@/gf/components/PickupField'
import TextArea from '@/gf/components/TextArea'
import Tooltip from '@/gf/components/Tooltip'

const NewStoreOrderCard = ({
  newStoreOrder,
  suggestedShippingCost,
  prepAndOpenAvailabilityModal,
  prepAndOpenAdjustQuantityModal,
  prepAndOpenAdjustUnitPriceModal,
  prepAndOpenShippingModal,
  removeAction,
  timingDetails,
  adjustTimingDetails,
  pickup,
  adjustPickup,
  prepAndOpenAltMpnModal,
  pickupAddress,
  setPickupAddress,
}: {
  newStoreOrder: NewStoreOrder
  suggestedShippingCost?: Maybe<Money>
  prepAndOpenAvailabilityModal: (lineItem: EditLineItem) => void
  prepAndOpenAdjustQuantityModal: (lineItem: EditLineItem) => void
  prepAndOpenAdjustUnitPriceModal: (lineItem: EditLineItem) => void
  prepAndOpenShippingModal: (storeId: string) => void
  removeAction: (lineItem: EditLineItem) => void
  timingDetails: string
  adjustTimingDetails: (newTimingDetails: string) => void
  pickup: boolean
  adjustPickup: (newPickup: boolean) => void
  prepAndOpenAltMpnModal: (lineItem: EditLineItem) => void
  pickupAddress: Address | null
  setPickupAddress: (address: Address) => void
}) => {
  const config = useConfig()
  const { user } = useSession()

  const subtotal = newStoreOrder.lineItems
    .map((li) => li.extendedPrice)
    .reduce(MoneyM.add, MoneyM.fromDecimal(0, 'USD'))

  const totalWithoutConvenienceFee = [
    subtotal,
    newStoreOrder.shippingCost || suggestedShippingCost || MoneyM.fromString('0', 'USD'),
  ].reduce(MoneyM.add)

  const { convenienceFeeRate } = config
  const convenienceFee = MoneyM.mult(totalWithoutConvenienceFee, convenienceFeeRate)
  const total = MoneyM.add(totalWithoutConvenienceFee, convenienceFee)

  return (
    <Card title={newStoreOrder.store.name}>
      <Card.Section>
        {newStoreOrder.lineItems.map((item, index) => (
          <Card.SubSection key={item.id ?? `${item.product.id}-${index}`}>
            <div className="flex space-x-4">
              <img
                src={ProductM.primaryImageURL(item.product, 'card')}
                alt={item.product.name}
                className="w-16 h-16 border rounded-sm overflow-hidden text-xs"
              />
              <div className="flex flex-1 flex-col">
                <p className="text-sm text-gray-900 font-bold">{item.product.name}</p>
                <p className="text-sm text-gray-500">{`MPN: ${item.product.mpn}`}</p>
                {item.inStock ? (
                  <InStockPill className="self-start" />
                ) : item.inStock !== null && item.availableAt ? (
                  <div className="flex flex-row justify-start items-center space-x-1 sm:space-x-1.5">
                    <LeadTimePill availableAt={item.availableAt} />
                    <Tooltip
                      text="This is the estimated date shared with the customer to indicate when the part will be ready"
                      size="medium"
                    />
                  </div>
                ) : null}
                {User.isAdmin(user) && item.product.altMpn ? (
                  <p className="text-xs text-gray-500">{`ALT MPN: ${item.product.altMpn}`}</p>
                ) : (
                  <></>
                )}
                <div className="flex w-full mt-2 text-sm">
                  <div className="flex space-x-4 w-full w-3/4">
                    <LinkButton
                      type="button"
                      onClick={() => {
                        prepAndOpenAdjustQuantityModal(item)
                      }}
                    >
                      Adjust Quantity
                    </LinkButton>
                    <LinkButton
                      type="button"
                      onClick={() => {
                        prepAndOpenAdjustUnitPriceModal(item)
                      }}
                    >
                      Adjust Price
                    </LinkButton>
                    <LinkButton
                      type="button"
                      onClick={() => {
                        removeAction(item)
                      }}
                    >
                      Remove Item
                    </LinkButton>
                    {User.isAdmin(user) ? (
                      <LinkButton
                        type="button"
                        onClick={() => {
                          prepAndOpenAltMpnModal(item)
                        }}
                      >
                        Edit Alt Mpn
                      </LinkButton>
                    ) : (
                      <></>
                    )}
                  </div>
                  <div className="flex w-full w-1/4 justify-end">
                    <Checkbox
                      checked={!!item.inStock}
                      onChange={() => prepAndOpenAvailabilityModal(item)}
                      className="whitespace-nowrap"
                      color="blue"
                    >
                      <span className="text-sm text-gray-700">
                        Available {!pickup ? 'to' : 'for'}{' '}
                        <span className="text-gray-900 font-medium">
                          {pickup ? 'will call' : 'ship'}
                        </span>{' '}
                        today
                      </span>
                    </Checkbox>
                  </div>
                </div>
              </div>
              <div className="text-sm text-gray-700 text-right">
                {newStoreOrder.store.discount ? (
                  <span className="space-x-2">
                    <span className="line-through text-gray-500">
                      {MoneyM.format(item.unitPrice)}
                    </span>
                    <span>{MoneyM.format(item.discountedUnitPrice)}</span>
                  </span>
                ) : (
                  MoneyM.format(item.unitPrice)
                )}{' '}
                x {item.quantity}
              </div>
              <div className="text-sm text-gray-700 text-right">
                {MoneyM.format(item.extendedPrice)}
              </div>
            </div>
          </Card.SubSection>
        ))}
      </Card.Section>
      <Card.Section>
        <div className="space-y-1 text-sm text-gray-700">
          <div className="flex justify-between">
            <span className="space-x-4">
              <span>Subtotal</span>
              <span>{`${newStoreOrder.lineItems.length} ${pluralize(
                'items',
                newStoreOrder.lineItems.length
              )}`}</span>
            </span>
            <span>{MoneyM.format(subtotal)}</span>
          </div>
          {!pickup && (
            <div className="flex justify-between">
              <span className="space-x-4">
                <span>Shipping</span>
                <LinkButton
                  type="button"
                  onClick={() => prepAndOpenShippingModal(newStoreOrder.store.id)}
                >
                  Adjust Shipping
                </LinkButton>
              </span>
              <span>
                {newStoreOrder.shippingCost
                  ? MoneyM.format(newStoreOrder.shippingCost || MoneyM.fromString('0', 'USD'))
                  : suggestedShippingCost
                    ? MoneyM.format(suggestedShippingCost)
                    : '(Calculated after creating the order)'}
              </span>
            </div>
          )}
          {!MoneyM.isZero(convenienceFee) && (
            <div className="flex justify-between">
              <span>Customer Fee</span>
              <span>{MoneyM.format(convenienceFee)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm text-gray-700 font-bold">
            <span>Total</span>
            <span>{MoneyM.format(total)}</span>
          </div>
        </div>
      </Card.Section>
      <Card.Section>
        <div className="space-y-6">
          <Field
            label="Expected Shipping/Lead Time (optional)"
            desc={
              <>
                <span className="font-medium">Note:</span> This information is shared with the
                customer.
              </>
            }
          >
            <TextArea value={timingDetails} onChange={(e) => adjustTimingDetails(e.target.value)} />
          </Field>
          <PickupField pickup={pickup} onChange={adjustPickup} />
          {pickup && (
            <div className="space-y-4">
              <h4>Pickup Address</h4>
              <AddressInput address={pickupAddress || undefined} onChange={setPickupAddress} />
            </div>
          )}
        </div>
      </Card.Section>
    </Card>
  )
}

export default NewStoreOrderCard
